import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const Email = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage('Please enter your email address');
      setMessageType('error');
      return;
    }

    if (!validateEmail(email)) {
      setMessage('Please enter a valid email address');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      // Save to Firestore subscriber list
      await addDoc(collection(db, 'newsletter'), {
        email: email.trim().toLowerCase(),
        name: name.trim() || 'Anonymous',
        timestamp: serverTimestamp(),
        subscribed: true,
      });

      // Send confirmation email via EmailJS (if configured)
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      if (serviceId && templateId && publicKey) {
        await emailjs.send(
          serviceId,
          templateId,
          {
            to_email: email.trim().toLowerCase(),
            to_name: name.trim() || 'friend',
            from_name: "Abel's Web",
          },
          publicKey
        );
      }

      setMessage('🎉 Successfully subscribed to the newsletter!');
      setMessageType('success');
      setEmail('');
      setName('');

      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    } catch (error) {
      setMessage('❌ Failed to subscribe. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <Title>📧 Newsletter Signup</Title>
      <Subtitle>
        Stay updated with my latest content, updates, and exclusive content!
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name (Optional):</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter your name"
            disabled={isSubmitting}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">
            Email Address: <Required>*</Required>
          </Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            required
            disabled={isSubmitting}
          />
        </FormGroup>

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Subscribing...' : 'Subscribe to Newsletter'}
        </SubmitButton>

        {message && <Message type={messageType}>{message}</Message>}
      </Form>
    </Wrapper>
  );
};

export default Email;

const Wrapper = styled.div`
  margin: 50px auto;
  margin-top: 100px;
  max-width: 800px;
  padding: 50px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  box-shadow:
    0 8px 30px rgba(0, 0, 0, 0.8),
    0 0 40px rgba(76, 0, 101, 0.3);
  border: 2px solid rgba(76, 0, 101, 0.5);

  /* Alienware Aurora 16X optimization (2560x1600) */
  @media (min-width: 2560px) {
    margin: 80px auto;
    margin-top: 150px;
    max-width: 1200px;
    padding: 70px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    margin: 60px auto;
    margin-top: 120px;
    max-width: 1000px;
    padding: 60px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    margin: 20px auto;
    margin-top: 50px;
    padding: 30px 20px;
    max-width: 95%;
  }

  @media (max-width: 390px) {
    margin: 15px auto;
    margin-top: 40px;
    padding: 25px 18px;
    max-width: 98%;
  }

  @media (max-width: 360px) {
    margin: 12px auto;
    margin-top: 35px;
    padding: 20px 15px;
    max-width: 100%;
  }
`;

const Title = styled.h2`
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
  text-shadow:
    2px 2px 8px #000000,
    0 0 20px rgba(76, 0, 101, 0.8);

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    font-size: 4.5rem;
    margin-bottom: 30px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    font-size: 3.8rem;
    margin-bottom: 25px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    font-size: 2rem;
    margin-bottom: 15px;
  }

  @media (max-width: 390px) {
    font-size: 1.8rem;
    margin-bottom: 12px;
  }

  @media (max-width: 360px) {
    font-size: 1.6rem;
    margin-bottom: 10px;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 40px;
  line-height: 1.6;
  text-shadow: 1px 1px 4px #000000;

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    font-size: 1.8rem;
    margin-bottom: 50px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    font-size: 1.5rem;
    margin-bottom: 45px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    font-size: 1rem;
    margin-bottom: 25px;
  }

  @media (max-width: 390px) {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
    margin-bottom: 18px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 30px;

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    gap: 40px;
  }

  @media (max-width: 430px) {
    gap: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  text-shadow: 2px 2px 6px #000000;

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    font-size: 2rem;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    font-size: 1.7rem;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    font-size: 1.2rem;
  }

  @media (max-width: 390px) {
    font-size: 1.1rem;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
  }
`;

const Required = styled.span`
  color: #ff4444;
  font-size: 1.2em;
`;

const Input = styled.input`
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  border: 3px solid rgba(76, 0, 101, 0.5);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4c0065;
    box-shadow: 0 0 20px rgba(76, 0, 101, 0.6);
    background: rgba(0, 0, 0, 0.8);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    padding: 25px;
    font-size: 1.6rem;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    padding: 22px;
    font-size: 1.4rem;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    padding: 15px;
    font-size: 1.1rem;
  }

  @media (max-width: 390px) {
    padding: 12px;
    font-size: 1rem;
  }

  @media (max-width: 360px) {
    padding: 10px;
    font-size: 0.95rem;
  }
`;

const SubmitButton = styled.button`
  padding: 20px 40px;
  background-color: #4c0065;
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  align-self: center;
  min-width: 250px;

  &:hover:not(:disabled) {
    background-color: #6a0088;
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(76, 0, 101, 0.6);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    padding: 25px 50px;
    font-size: 2rem;
    min-width: 350px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    padding: 22px 45px;
    font-size: 1.7rem;
    min-width: 300px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    padding: 15px 30px;
    font-size: 1.2rem;
    min-width: 200px;
  }

  @media (max-width: 390px) {
    padding: 12px 25px;
    font-size: 1.1rem;
    min-width: 180px;
  }

  @media (max-width: 360px) {
    padding: 10px 20px;
    font-size: 1rem;
    min-width: 160px;
  }
`;

const Message = styled.div`
  padding: 15px 20px;
  border-radius: 10px;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.2rem;
  text-align: center;
  background: ${props =>
    props.type === 'success'
      ? 'rgba(0, 255, 0, 0.2)'
      : 'rgba(255, 68, 68, 0.2)'};
  border: 2px solid
    ${props =>
      props.type === 'success'
        ? 'rgba(0, 255, 0, 0.5)'
        : 'rgba(255, 68, 68, 0.5)'};
  color: ${props => (props.type === 'success' ? '#00ff00' : '#ff4444')};
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);

  /* Alienware Aurora 16X optimization */
  @media (min-width: 2560px) {
    padding: 20px 25px;
    font-size: 1.5rem;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    padding: 18px 22px;
    font-size: 1.3rem;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    padding: 12px 15px;
    font-size: 1rem;
  }

  @media (max-width: 390px) {
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  @media (max-width: 360px) {
    padding: 8px 10px;
    font-size: 0.85rem;
  }
`;
